import type { ShellAgentId } from "@/lib/shell-agent";

// Real install commands — verified against the published packages. A broken
// paste is worse than no onboarding, so keep these in lockstep with
// `OnboardingFlow`'s historical strings and the sibling CLI/skills repos.
export const CLI_INSTALL_COMMAND = "npm install -g @ano-chat/cli";

export const SKILLS_INSTALL_COMMAND =
  "claude plugin marketplace add ano-chat/ano-skills && claude plugin install ano-skills@ano-skills";

const CLAUDE_DOCS_URL = "https://docs.anthropic.com/en/docs/claude-code";

/**
 * Strip shell-active characters from a user-derived name before embedding it in
 * the finale prompt. The prompt is typed into the running agent's REPL — but if
 * the agent isn't up it falls through to the bare shell, where backticks / `$()`
 * in a display name would otherwise execute. We only need a plain name here.
 */
export function sanitizeNameForPrompt(name: string): string {
  // Strip shell-active chars (command subst, quoting, redirection, globbing,
  // brace/tilde/history expansion) so a shell fallback can't be injected.
  return name
    .replace(/[`$\\;|&()<>"'!{}*~[\]?]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

export type ConnectAgentStepId = "launch" | "cli" | "skills" | "finale";

export interface ConnectAgentStep {
  id: ConnectAgentStepId;
  title: string;
  description: string;
  command: string;
  runLabel: string;
  note?: string;
  docsUrl?: string;
}

/**
 * Build the agent-connection steps for the chosen shell agent.
 *
 * The flow is, in order: launch the user's OWN agent → install the Ano CLI →
 * (Claude only) add the Ano Skills plugin → ask the agent to say hi in #general.
 * There is no Ano-managed bot anywhere — every step wires up the user's own code
 * agent (Claude Code / Codex / Gemini).
 *
 * The "skills" step only applies to Claude Code: Ano Skills is a Claude Code
 * marketplace plugin, so Codex/Gemini skip it.
 *
 * The finale is a NATURAL-LANGUAGE PROMPT typed to the now-running agent — not a
 * raw CLI command. The agent uses its Ano skills/CLI to post to #general (as the
 * user), so the message lands and the dialog's watcher celebrates.
 */
export function buildConnectAgentSteps(opts: {
  agentId: ShellAgentId;
  agentLabel: string;
  agentCommand: string;
  finaleMessage: string;
}): ConnectAgentStep[] {
  const { agentId, agentLabel, agentCommand, finaleMessage } = opts;

  const steps: ConnectAgentStep[] = [
    {
      id: "launch",
      title: `Launch ${agentLabel}`,
      description: `Start ${agentLabel} in the built-in shell. This is your own coding agent — Ano never runs it for you.`,
      command: agentCommand,
      runLabel: `Launch ${agentLabel}`,
      docsUrl: agentId === "claude" ? CLAUDE_DOCS_URL : undefined,
    },
    {
      id: "cli",
      title: "Install the Ano CLI",
      description:
        "The Ano CLI lets your agent read channels and post messages. It signs in automatically the next time you open a workspace.",
      command: CLI_INSTALL_COMMAND,
      runLabel: "Send to Shell",
    },
  ];

  if (agentId === "claude") {
    steps.push({
      id: "skills",
      title: "Add Ano Skills to Claude Code",
      description:
        "Ano Skills teach Claude Code how to talk to your workspace — read channels, send messages, manage tasks.",
      command: SKILLS_INSTALL_COMMAND,
      runLabel: "Send to Shell",
      note: "Requires Claude Code from step 1.",
    });
  }

  steps.push({
    id: "finale",
    title: "Say hi in #general",
    description:
      "Ask your agent to introduce itself — it posts to #general with its Ano skills. Watch it pop in on the right.",
    command: finaleMessage,
    runLabel: "Ask your agent",
  });

  return steps;
}

/**
 * The natural-language instruction handed to the agent for the finale — phrased
 * the way a person would ask their own agent to introduce itself.
 */
export function buildFinaleMessage(firstName?: string): string {
  const safe = sanitizeNameForPrompt(firstName ?? "");
  const who = safe ? `${safe}'s` : "My";
  return `Say hi to the team in #general 👋 Introduce yourself as ${who} coding agent — now connected to Ano and ready to help.`;
}
